/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { useEffect, useState } from "react";
import {
  CatalogPersonType,
  CivilStatus,
  MatrimonialRegimesData,
  PayloadPersonPatch,
  PersonaStructure,
} from "@interfaces/index";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { MATRIMONIAL_REGIMEN_CODES, PERSON_CATALOG } from "@helpers/constantes";
import * as Sentry from "@sentry/react";
import React from "react";
import {
  useLazyGetPersonByIdGuaranteeQuery,
  useLazyGetPersonsSecondGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  usePostPersonSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import {
  useLazyGetCivilStatusSecondGuaranteeQuery,
  useLazyGetMatrimonialRegimesSecondGuaranteeQuery,
} from "@api/second_guarantee_api/catalogsSecondGuarantee";
import { UUID } from "crypto";
import { useLazyGetPersonTypeQuery } from "@api/catalogs";

const OTHER_PERSON_ID = "1-2-3-4-5";

const InformacionSegundoAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const personAval = useSelector((state: RootState) => state.secondGuarante.guarantee);

  const [getEstadoCivil] = useLazyGetCivilStatusSecondGuaranteeQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesSecondGuaranteeQuery();
  const [patchPerson] = usePatchPersonSecondGuaranteeMutation();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();
  const [getPersons] = useLazyGetPersonsSecondGuaranteeQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();
  const [postPerson] = usePostPersonSecondGuaranteeMutation();

  const [listEdoCivil, setListEdoCivil] = useState<CivilStatus[]>([]);
  const [listMatrimonialRegimen, setListMatrimonialRegimen] = useState<MatrimonialRegimesData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [idEdoCivilCasado, setIdEdoCivilCasado] = useState<string>("");
  const [otherPersons, setOtherPersons] = useState<PersonaStructure[]>([]);
  const [personTypeConyugeAval, setPersonTypeConyugeAval] = useState<CatalogPersonType | null>(null);

  const formik = useFormik({
    initialValues: {
      ocupacion: "",
      estado_civil_id: "",
      matrimonial_regimes_id: "",
      conyuge_id: "",
      conyuge_name: "",
      conyuge_last_name: "",
      conyuge_last_name_2: "",
    },
    validationSchema: yup.object().shape({
      ocupacion: yup.string().required("Campo requerido"),
      estado_civil_id: yup.string().required("Campo requerido"),
      matrimonial_regimes_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
      conyuge_last_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_last_name_2: yup.string(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        const list_persons = await getPersons({
          loanApplicationId: String(personAval.loan_application_id),
        }).unwrap();

        const is_married = values.estado_civil_id === idEdoCivilCasado;
        let person_conyuge: PersonaStructure | null = null;

        if (is_married) {
          // si es casado, se valida si se selecciono otra persona, si es asi, se crea un nuevo conyuge
          if (values.conyuge_id === OTHER_PERSON_ID) {
            const response_create = await postPerson({
              body: {
                loan_application_id: String(personAval.loan_application_id),
                person_types: [String(personTypeConyugeAval?.id)],
                name: values.conyuge_name,
                last_name: values.conyuge_last_name,
                last_name_2: values.conyuge_last_name_2,
              },
            }).unwrap();
            person_conyuge = response_create.data;
          } else {
            // obtener la informacion del conyuge
            person_conyuge = list_persons.find((item) => item.id === values.conyuge_id) || null;
            if (!person_conyuge) {
              throw new Error("No se encontró el conyuge");
            }

            // obtener los tipos de persona del conyuge
            const person_types = person_conyuge.person_types_detail.map((item) => item.id);

            // validar si el conyuge tiene el tipo de persona 'conyuge aval', en caso de no tenerlo, se agrega
            if (!person_types.includes(String(personTypeConyugeAval?.id) as UUID)) {
              await patchPerson({
                personId: person_conyuge.id,
                body: {
                  person_types: [...person_types, String(personTypeConyugeAval?.id)],
                },
              }).unwrap();
            }
          }
        }

        const payload: PayloadPersonPatch = {
          occupation: values.ocupacion,
          civil_status_id: values.estado_civil_id,
          matrimonial_regimes_id: values.estado_civil_id === idEdoCivilCasado ? values.matrimonial_regimes_id : null,
          spouse_id: null,
        };

        if (is_married) {
          if (!person_conyuge) {
            throw new Error("No se encontró el conyuge");
          }
          payload.matrimonial_regimes_id = values.matrimonial_regimes_id;
          payload.spouse_id = person_conyuge.id;
        }

        await patchPerson({
          personId: String(personAval.id),
          body: payload,
        }).unwrap();

        navigate("/avales/informacion_aval_2");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        if (!personAval) {
          navigate("/avales/expediente_digital_segundo_aval");
          return;
        }

        const [res_aval, res_edo_civil, res_edo_matrimonial, res_persons, res_person_type] = await Promise.all([
          getPerson(String(personAval.id)).unwrap(),
          getEstadoCivil().unwrap(),
          getMatrimonialRegimen().unwrap(),
          getPersons({ loanApplicationId: String(personAval.loan_application_id) }).unwrap(),
          getPersonType().unwrap(),
        ]);

        const other_persons = [];
        const conyuges_persons = [];

        // obtener las personas diferentes al aval seleccionado y los conyuges
        for (const person of res_persons) {
          if (person.id !== personAval.id) {
            other_persons.push(person);

            const person_types = person.person_types_detail.map((item) => item.code);
            if (person_types.includes(PERSON_CATALOG.segundo_aval.code)) {
              conyuges_persons.push(person);
            }
          }
        }

        const other_person = {
          id: OTHER_PERSON_ID,
          name: "Otro",
          last_name: "",
          last_name_2: "",
          person_types_detail: [],
        } as unknown as PersonaStructure;

        other_persons.push(other_person);

        // buscar id para el estado civil "casado"
        const edo_civil_casado: CivilStatus | undefined = res_edo_civil.find(
          (item) => item.name.toLowerCase() === "casado",
        );
        // buscar id para el regimen "sociedad conyugal"
        const regimen_sociedad_conyugal = res_edo_matrimonial.find(
          (item) => item.code === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code,
        );

        if (!edo_civil_casado || !regimen_sociedad_conyugal) {
          throw new Error("No se encontraron los datos necesarios para continuar.");
        }

        const person_type_conyuge_aval = res_person_type.find((item) => item.code === PERSON_CATALOG.conyuge_aval.code);

        setIdEdoCivilCasado(edo_civil_casado.id);
        setListEdoCivil(res_edo_civil);
        setListMatrimonialRegimen(res_edo_matrimonial);
        setOtherPersons(other_persons);
        setPersonTypeConyugeAval(person_type_conyuge_aval || null);

        formik.setFieldValue("estado_civil_id", res_aval?.civil_status_id || "");
        formik.setFieldValue("matrimonial_regimes_id", res_aval?.matrimonial_regimes_id || "");
        formik.setFieldValue("ocupacion", res_aval?.occupation || "");

         // si es casado y se encuentra una persona de tipo "conyuge aval"
        if (res_aval?.civil_status_id === edo_civil_casado.id && res_aval?.spouse_id) {
          formik.setFieldValue("conyuge_id", res_aval.spouse_id);
        } else {
          formik.setFieldValue("conyuge_id", "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
          >
            <strong>1</strong> de 2
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {personAval.personalInfo?.name} {personAval.personalInfo?.first_lastname}{" "}
            {personAval.personalInfo?.second_lastname}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <TextField
              id="ocupacion"
              name="ocupacion"
              label="Ocupación"
              margin="normal"
              required
              fullWidth
              autoComplete="off"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
              helperText={formik.touched.ocupacion && formik.errors.ocupacion}
              value={formik.values.ocupacion}
              disabled={isLoading || formik.isSubmitting}
            />

            <FormControl
              fullWidth
              required
              sx={{
                ...customStylesAsterisk,
                mt: 2,
              }}
              error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
            >
              <InputLabel id="estado_civil_id">Estado civil</InputLabel>
              <Select
                labelId="estado_civil_id"
                id="estado_civil_id"
                name="estado_civil_id"
                fullWidth
                required
                label="Estado civil"
                value={formik.values.estado_civil_id}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
                disabled={isLoading || formik.isSubmitting}
              >
                <MenuItem value={""}>Selecciona</MenuItem>
                {listEdoCivil.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.estado_civil_id && formik.errors.estado_civil_id && (
                <FormHelperText>{formik.errors.estado_civil_id}</FormHelperText>
              )}
            </FormControl>

            {formik.values.estado_civil_id !== "" && formik.values.estado_civil_id === idEdoCivilCasado && (
              <>
                <FormControl
                  fullWidth
                  required
                  sx={{
                    ...customStylesAsterisk,
                    mt: 2,
                  }}
                  error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                >
                  <InputLabel id="matrimonial_regimes_id">Régimen matrimonial</InputLabel>
                  <Select
                    labelId="matrimonial_regimes_id"
                    id="matrimonial_regimes_id"
                    name="matrimonial_regimes_id"
                    fullWidth
                    required
                    label="Régimen matrimonial *"
                    value={formik.values.matrimonial_regimes_id}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {listMatrimonialRegimen.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.matrimonial_regimes_id && formik.errors.matrimonial_regimes_id && (
                    <FormHelperText>{formik.errors.matrimonial_regimes_id}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  required
                  sx={{
                    ...customStylesAsterisk,
                    mt: 2,
                  }}
                  error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                >
                  <InputLabel id="conyuge_id">Conyuge</InputLabel>
                  <Select
                    labelId="conyuge_id"
                    id="conyuge_id"
                    name="conyuge_id"
                    fullWidth
                    required
                    label="Conyuge"
                    value={formik.values.conyuge_id}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {otherPersons.map((item: PersonaStructure) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name} {item.last_name} {item.last_name_2}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.conyuge_id && formik.errors.conyuge_id && (
                    <FormHelperText>{formik.errors.conyuge_id}</FormHelperText>
                  )}
                </FormControl>
              </>
            )}

            {formik.values.conyuge_id !== "" && formik.values.conyuge_id === OTHER_PERSON_ID && (
              <React.Fragment>
                <FormControl fullWidth>
                  <TextField
                    id="conyuge_name"
                    name="conyuge_name"
                    label="Nombre del conyuge"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.conyuge_name && Boolean(formik.errors.conyuge_name)}
                    helperText={formik.touched.conyuge_name && formik.errors.conyuge_name}
                    value={formik.values.conyuge_name}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    id="conyuge_last_name"
                    name="conyuge_last_name"
                    label="Apellido paterno del conyuge"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.conyuge_last_name && Boolean(formik.errors.conyuge_last_name)}
                    helperText={formik.touched.conyuge_last_name && formik.errors.conyuge_last_name}
                    value={formik.values.conyuge_last_name}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    id="conyuge_last_name_2"
                    name="conyuge_last_name_2"
                    label="Apellido materno del conyuge"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.conyuge_last_name_2 && Boolean(formik.errors.conyuge_last_name_2)}
                    helperText={formik.touched.conyuge_last_name_2 && formik.errors.conyuge_last_name_2}
                    value={formik.values.conyuge_last_name_2}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </FormControl>
              </React.Fragment>
            )}

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/avales/expediente_digital_segundo_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                  sx={{ padding: "0px", height: "100%" }}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button variant="default" type="submit" fullWidth disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionSegundoAval;
